import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEdit,
  faTrash,
  faSave,
  faPlus,
  faMinus,
  faBoxArchive,
  faBoxOpen,
  faRepeat,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

function PaymentDetailModal({ payment, onClose, onUpdate, onDelete }) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [incrementValue, setIncrementValue] = useState(1);

  // Edit form state
  const [editData, setEditData] = useState({
    name: payment.name,
    type: payment.type || "recurrent",
    amount: payment.amount,
    totalAmount: payment.totalAmount || 0,
    totalMonths: payment.totalMonths || 0,
    paidMonths: payment.paidMonths || 0,
    installmentSource: payment.installmentSource || "personal",
    currency: payment.currency,
  });

  const isInstallment = payment.type === "installment";
  const percentage = isInstallment
    ? (payment.paidMonths / payment.totalMonths) * 100
    : 0;
  const remainingAmount = isInstallment
    ? payment.totalAmount - payment.paidMonths * payment.amount
    : 0;

  // Calculate monthly payment preview for edit mode
  const editCalc = useMemo(() => {
    if (
      editData.type !== "installment" ||
      !editData.totalAmount ||
      !editData.totalMonths
    )
      return null;
    const monthly =
      Math.ceil(
        (parseFloat(editData.totalAmount) / parseInt(editData.totalMonths)) *
          100
      ) / 100;
    return { monthlyAmount: monthly };
  }, [editData.type, editData.totalAmount, editData.totalMonths]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: editData.name,
        currency: editData.currency,
      };

      if (editData.type === "installment") {
        payload.type = "installment";
        payload.totalAmount = parseFloat(editData.totalAmount);
        payload.totalMonths = parseInt(editData.totalMonths);
        payload.paidMonths = parseInt(editData.paidMonths);
        payload.installmentSource = editData.installmentSource;
      } else {
        payload.type = "recurrent";
        payload.amount = parseFloat(editData.amount);
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/monthly-payments/${payment.id}`,
        payload
      );
      onUpdate(response.data);
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/monthly-payments/${payment.id}`
      );
      onDelete(payment.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setLoading(false);
    }
  };

  const handleIncrementPaid = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/monthly-payments/${payment.id}/increment-paid`,
        { increment: incrementValue }
      );
      onUpdate(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArchive = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/monthly-payments/${payment.id}/archive`,
        { archived: !payment.archived }
      );
      onUpdate(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 transition-colors max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800 transition-colors sticky top-0 z-10">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FontAwesomeIcon
              icon={isInstallment ? faCreditCard : faRepeat}
              className={isInstallment ? "text-purple-500" : "text-blue-500"}
            />
            {isEditing ? t("editPayment", "Редагувати платіж") : payment.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {isEditing ? (
            /* Edit Mode */
            <>
              {/* Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t("name", "Назва")}
                </label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                />
              </div>

              {editData.type === "recurrent" ? (
                /* Recurrent Edit Fields */
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                      {t("amount", "Сума")}
                    </label>
                    <input
                      type="number"
                      value={editData.amount}
                      onChange={(e) =>
                        setEditData({ ...editData, amount: e.target.value })
                      }
                      className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                      {t("currency", "Валюта")}
                    </label>
                    <select
                      value={editData.currency}
                      onChange={(e) =>
                        setEditData({ ...editData, currency: e.target.value })
                      }
                      className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                    >
                      <option value="EUR">EUR</option>
                      <option value="UAH">UAH</option>
                      <option value="USD">USD</option>
                      <option value="HUF">HUF</option>
                    </select>
                  </div>
                </div>
              ) : (
                /* Installment Edit Fields */
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                        {t("totalAmount", "Загальна сума")}
                      </label>
                      <input
                        type="number"
                        value={editData.totalAmount}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            totalAmount: e.target.value,
                          })
                        }
                        className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                        {t("currency", "Валюта")}
                      </label>
                      <select
                        value={editData.currency}
                        onChange={(e) =>
                          setEditData({ ...editData, currency: e.target.value })
                        }
                        className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                      >
                        <option value="EUR">EUR</option>
                        <option value="UAH">UAH</option>
                        <option value="USD">USD</option>
                        <option value="HUF">HUF</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                        {t("totalMonths", "Всього місяців")}
                      </label>
                      <input
                        type="number"
                        value={editData.totalMonths}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            totalMonths: e.target.value,
                          })
                        }
                        className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                        {t("paidMonths", "Сплачено місяців")}
                      </label>
                      <input
                        type="number"
                        value={editData.paidMonths}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            paidMonths: e.target.value,
                          })
                        }
                        className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                        min="0"
                        max={editData.totalMonths}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                      {t("source", "Джерело")}
                    </label>
                    <select
                      value={editData.installmentSource}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          installmentSource: e.target.value,
                        })
                      }
                      className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                    >
                      <option value="personal">
                        {t("personal", "Особисті")}
                      </option>
                      <option value="bank">{t("bank", "Банк")}</option>
                    </select>
                  </div>

                  {/* Calculated Monthly Amount Preview */}
                  {editCalc && (
                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t("calculatedMonthly", "Розрахований щомісячний")}:
                        </span>
                        <span className="font-bold text-primary-600 dark:text-primary-400">
                          {editCalc.monthlyAmount} {editData.currency}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            /* View Mode */
            <>
              {/* Amount Display */}
              <div className="text-center py-4">
                <p className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                  {payment.amount} {payment.currency}
                </p>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {t("perMonth", "на місяць")}
                </p>
              </div>

              {/* Installment Details */}
              {isInstallment && (
                <div className="space-y-4">
                  {/* Progress */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {t("progress", "Прогрес")}
                      </span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {payment.paidMonths} / {payment.totalMonths}{" "}
                        {t("months", "міс.")}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          percentage >= 100
                            ? "bg-green-500"
                            : percentage >= 66
                            ? "bg-green-500"
                            : percentage >= 33
                            ? "bg-orange-500"
                            : "bg-red-500"
                        }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {t("totalAmount", "Загальна сума")}
                      </span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {payment.totalAmount} {payment.currency}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {t("remaining", "Залишилось")}
                      </span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {remainingAmount.toFixed(2)} {payment.currency}
                      </span>
                    </div>
                  </div>

                  {/* Quick Increment */}
                  {!payment.archived &&
                    payment.paidMonths < payment.totalMonths && (
                      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t("markMonthsPaid", "Позначити місяці як сплачені")}
                        </p>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              setIncrementValue(Math.max(1, incrementValue - 1))
                            }
                            className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                          <input
                            type="number"
                            value={incrementValue}
                            onChange={(e) =>
                              setIncrementValue(
                                Math.max(
                                  1,
                                  Math.min(
                                    payment.totalMonths - payment.paidMonths,
                                    parseInt(e.target.value) || 1
                                  )
                                )
                              )
                            }
                            className="w-16 text-center p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl font-bold text-lg dark:text-white"
                            min="1"
                            max={payment.totalMonths - payment.paidMonths}
                          />
                          <button
                            onClick={() =>
                              setIncrementValue(
                                Math.min(
                                  payment.totalMonths - payment.paidMonths,
                                  incrementValue + 1
                                )
                              )
                            }
                            className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                          <button
                            onClick={handleIncrementPaid}
                            disabled={loading}
                            className="flex-1 btn-primary py-2 rounded-xl font-medium flex items-center justify-center gap-2"
                          >
                            {loading ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faPlus} />
                                {t("addPaid", "Додати")}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </>
          )}

          {/* Error */}
          {error && (
            <div className="text-red-500 text-center py-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Delete Confirmation */}
          {deleteConfirm && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                {t(
                  "confirmDelete",
                  "Ви впевнені, що хочете видалити цей платіж?"
                )}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-lg font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t("cancel", "Скасувати")}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 py-2 rounded-lg font-medium text-white bg-red-500 hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} />
                      {t("delete", "Видалити")}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {!deleteConfirm && (
            <div className="flex gap-3 pt-2">
              {isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="flex-1 py-3 rounded-xl font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t("cancel", "Скасувати")}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 btn-primary py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} />
                        {t("save", "Зберегти")}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setDeleteConfirm(true)}
                    className="p-3 rounded-xl text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                  {isInstallment && (
                    <button
                      onClick={handleToggleArchive}
                      disabled={loading}
                      className={`p-3 rounded-xl transition-colors ${
                        payment.archived
                          ? "text-green-600 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40"
                          : "text-amber-600 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={payment.archived ? faBoxOpen : faBoxArchive}
                      />
                    </button>
                  )}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 btn-primary py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    {t("edit", "Редагувати")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PaymentDetailModal;
